import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../../store';
import { getUserType } from '../../../store/actions/ChatAction';
import { fetchAllEmployees } from '../../../store/actions/organization/employeeActions';
import Header from '../../shared/TailwindComponents/Header';
import Table from '../../shared/TailwindComponents/Table';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FilterBarWithTabs from '../../shared/TailwindComponents/FilterBarWithTabs';
import PeopleForm from './PeopleForm';
import { PiArrowSquareInLight, PiArrowSquareOutLight, PiPlusLight, PiUsersLight } from 'react-icons/pi';
import { hasAccessToFeature } from '../../../utils/permissionUtils';
import ProfileImage from '../../Header/ProfileImage';
import Badge from '../../shared/TailwindComponents/Badge';
import { Tooltip } from '@mui/material';
import ExternalPeopleTab from './External/ExternalPeopleList';
import ExternalOnboarding from './External/ExternalOnboarding';
import Button from '../../shared/TailwindComponents/Button';
import debounce from 'lodash.debounce';
import SkeletonClientItem from '../../Clients/SkeletonClientItem';
import { fetchAllDepartments } from '../../../store/actions/organization/DepartmentActions';
import { formatDate, formatRole } from '../../../utils/functions';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, peopleListTourSteps } from '../../shared/FeatureTour';

export interface EmployeeRow {
  id: string;
  Name: React.ReactNode;
  Role: React.ReactNode;
  Departments: React.ReactNode;
  Teams: React.ReactNode;
  'Join Date': string;
}

interface EmployeeListProps {
  isAddingEmployee?: boolean;
  setIsAddingEmployee?: (value: boolean) => void;
  subscriptionActive?: boolean;
}

type TabType = 'internal' | 'external';

const EmployeeGridItem: React.FC<{ employee: EmployeeRow; onClick: (id: string) => void }> = ({ employee, onClick }) => {
  const { t } = useTranslation();
  const handleClick = () => {
    onClick(employee.id);
  };
  return (
    <div
      className="p-4 bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer"
      onClick={handleClick}
    >
      <div className="flex items-center gap-3">
        <div className="flex-1">
          {typeof employee.Name === 'object' && employee.Name}
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 space-y-1">
            {employee.Role && (
              <div>
                <span className="text-gray-700 dark:text-gray-300">{t('Role')}:</span>
                <div className="mt-1">{employee.Role}</div>
              </div>
            )}
            {employee.Departments && (
              <div>
                <span className="text-gray-700 dark:text-gray-300">{t('Departments')}:</span>
                <div className="mt-1">{employee.Departments}</div>
              </div>
            )}
            {employee.Teams && (
              <div>
                <span className="text-gray-700 dark:text-gray-300">{t('Teams')}:</span>
                <div className="mt-1">{employee.Teams}</div>
              </div>
            )}
            <div>
              <span className="text-gray-700 dark:text-gray-300">{t('Joined')}:</span>
              <div className="mt-1">{formatDate(employee['Join Date'])}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EmployeeList: React.FC<EmployeeListProps> = ({ subscriptionActive, isAddingEmployee: isAddingEmployeeProp, setIsAddingEmployee: setIsAddingEmployeeProp }) => {
  const dispatch = useAppDispatch();
  const { loading, total } = useSelector((state: RootState) => state.employees);
  const [allEmployees, setAllEmployees] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({});
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [allDepartments, setAllDepartments] = useState<string[]>([]);
  const [allRoles, setAllRoles] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const userType = getUserType();
  const history = useHistory();
  const { t } = useTranslation();
  const employeesPerPage = 50;
  const [isAddingEmployee, setIsAddingEmployee] = useState(isAddingEmployeeProp || false);
  const [isAddingExternal, setIsAddingExternal] = useState(false);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const location = useLocation();
  const [hasMore, setHasMore] = useState(false);
  const [isFetching, setIsFetching] = useState(false); 
  const [activeTab, setActiveTab] = useState<TabType>(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('tab') as TabType) || 'internal';
  });
  const totalActiveFilters = Object.values(selectedFilters).reduce((acc, curr) => acc + curr.length, 0);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'people-list',
    steps: peopleListTourSteps,
    readyToShow: !loading && !isAddingEmployee && !isAddingExternal,
    autoStartDelay: 600,
  });

  const tabs = [
    { label: t('Internal'), value: 'internal', icon: <PiArrowSquareInLight className="h-4 w-4" /> },
    { label: t('External'), value: 'external', icon: <PiArrowSquareOutLight className="h-4 w-4" /> },
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') as TabType;
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [location.search, activeTab]);

  // useEffect(() => {
  //   const query = new URLSearchParams(location.search);
  //   if (query.get('add') === 'true') {
  //     setIsAddingEmployee(true);
  //     if (setIsAddingEmployeeProp) setIsAddingEmployeeProp(true);
  //   } else {
  //     setIsAddingEmployee(false);
  //     if (setIsAddingEmployeeProp) setIsAddingEmployeeProp(false);
  //   }
  // }, [location, setIsAddingEmployeeProp]);

  useEffect(() => {
    const fetchInitialData = async () => {
      const deptResp = await dispatch(fetchAllDepartments({ limit: 100 }));
      if (deptResp && Array.isArray(deptResp)) {
        const deptNames = deptResp.map((d: any) => d.Name || d.name).filter((d: any) => d);
        setAllDepartments(deptNames);
      } else if (deptResp && deptResp.departments && Array.isArray(deptResp.departments)) {
        const deptNames = deptResp.departments.map((d: any) => d.Name || d.name).filter((d: any) => d);
        setAllDepartments(deptNames);
      }
      const empResp = await dispatch(fetchAllEmployees({ limit: 1000, filters: { skip_filters: true } }));
      if (empResp && empResp.employees && Array.isArray(empResp.employees)) {
        const uniqueRoles = Array.from(new Set(empResp.employees.map(emp => emp.role || emp.role).filter(role => role)));
        setAllRoles(uniqueRoles);
      }
    };
    fetchInitialData();
  }, [dispatch]);

  const filters = useMemo(() => [
    {
      id: 'Department',
      name: t('Department'),
      options: allDepartments.map(dept => ({ value: dept, label: dept })),
    },
    {
      id: 'Role',
      name: t('Roles'),
      options: allRoles.map(role => ({ value: role, label: role })),
    },
  ], [allDepartments, allRoles, t]);

  const getQueryParams = useCallback(() => {
    const filtersObj: { [key: string]: string[] } = {};
    if (selectedFilters.Department && selectedFilters.Department.length > 0) {
      filtersObj.Department = selectedFilters.Department;
    }
    if (selectedFilters.Role && selectedFilters.Role.length > 0) {
      filtersObj.Roles = selectedFilters.Role;
    }
    return { searchTerm, filters: filtersObj, sortBy, sortOrder, limit: employeesPerPage };
  }, [searchTerm, sortBy, sortOrder, employeesPerPage, selectedFilters]);

  const refetchEmployees = useCallback(async (fetchPage: number = 1) => {
    setIsFetching(true);
    const params = getQueryParams();
    const fullResp = await dispatch(fetchAllEmployees({ ...params, page: fetchPage }));
    if (fullResp && fullResp.employees && Array.isArray(fullResp.employees)) {
      setAllEmployees(fullResp.employees);
      setHasMore(fullResp.employees.length === employeesPerPage && fullResp.currentPage < fullResp.totalPages);
    } else {
      setAllEmployees([]);
      setHasMore(false);
    }
    setIsFetching(false);
  }, [dispatch, getQueryParams, employeesPerPage]);

  const loadMoreEmployees = useCallback(
    debounce(async () => {
      if (!hasMore || loading || isFetching) return;
      setIsFetching(true);
      const nextPage = page + 1;
      const params = getQueryParams();
      const fullResp = await dispatch(fetchAllEmployees({ ...params, page: nextPage }));
      if (fullResp && fullResp.employees && Array.isArray(fullResp.employees)) {
        setAllEmployees(prev => [...prev, ...fullResp.employees]);
        setPage(nextPage);
        setHasMore(fullResp.employees.length === employeesPerPage && fullResp.currentPage < fullResp.totalPages);
      }
      setIsFetching(false);
    }, 300),
    [dispatch, getQueryParams, hasMore, loading, page, employeesPerPage]
  );

  useEffect(() => {
    if (isAddingEmployee || isAddingExternal || isFetching) return;
    setPage(1);
    refetchEmployees(1);
  }, [searchTerm, selectedFilters, sortBy, sortOrder, isAddingEmployee, isAddingExternal, refetchEmployees]);

  const handleAddEmployee = () => {
    setIsAddingEmployee(true);
    if (setIsAddingEmployeeProp) setIsAddingEmployeeProp(true);
    history.push('/people?add=true');
  };

  const handleCloseOnboarding = () => {
    setIsAddingEmployee(false);
    setIsAddingExternal(false);
    if (setIsAddingEmployeeProp) setIsAddingEmployeeProp(false);
    if (activeTab === 'internal') {
      history.push('/people');
    } else {
      history.push('/people?tab=external');
    }
  };

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters((prev) => ({ ...prev, [filterName]: selectedValues }));
    setPage(1);
  };

  const handleSortChange = (newSortBy: string, newSortOrder: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setPage(1);
  };

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      setSearchTerm(query);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (query: string) => {
    debouncedSearch(query);
  };

  const columns = ['Name', 'Role', 'Departments', 'Teams', 'Join Date'];

  const data = useMemo<EmployeeRow[]>(() => {
    if (!Array.isArray(allEmployees)) {
      return [];
    }
    const mappedData = allEmployees.map((employee): EmployeeRow | null => {
      if (!employee) {
        console.warn('Employee is undefined:', employee);
        return null;
      }
      const isActive = employee.is_active !== false;
      const invitationStatus = employee.invitation_status || (isActive ? 'active' : 'pending');
      const firstName = employee.first_name || employee.FirstName || '';
      const lastName = employee.last_name || employee.LastName || '';
      const email = employee.email || employee.Email || '';

      const displayName = firstName && lastName ? `${firstName} ${lastName}` : email;
      const initials = firstName && lastName
        ? `${firstName?.[0] || ''}${lastName?.[0] || ''}`.trim()
        : (email?.split('@')[0]?.[0] || '?').toUpperCase();

      const joinDateStr = employee.created_at ? formatDate(employee.created_at) : 'N/A';

      const statusBadge = invitationStatus === 'pending' ? (
        <Badge color="yellow" className="text-xs">{t('Invitation sent')}</Badge>
      ) : null;

      const depts = employee.departments && Array.isArray(employee.departments) ? employee.departments : [];
      const teams = employee.teams && Array.isArray(employee.teams) ? employee.teams : [];

      const departmentsList = depts.length > 0
        ? (
          <>
            {depts.slice(0, 2).map((d: any) => (
              <Badge key={d.id || d.name} color='gray'>{d.name}</Badge>
            ))}
            {depts.length > 2 && (
              <Tooltip title={depts.slice(2).map((d: any) => d.name).join(', ')} placement="top" arrow>
                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded cursor-default">+{depts.length - 2}</span>
              </Tooltip>
            )}
          </>
        )
        : <span className="text-gray-400 dark:text-gray-600 text-xs">—</span>;

      const teamsList = teams.length > 0
        ? (
          <>
            {teams.slice(0, 2).map((tm: any) => (
              <Badge key={tm.id || tm.name} color='gray'>{tm.name}</Badge>
            ))}
            {teams.length > 2 && (
              <Tooltip title={teams.slice(2).map((tm: any) => tm.name).join(', ')} placement="top" arrow>
                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded cursor-default">+{teams.length - 2}</span>
              </Tooltip>
            )}
          </>
        )
        : <span className="text-gray-400 dark:text-gray-600 text-xs">—</span>;

      return {
        id: employee.id || employee.UserID,
        Name: (
          <div className="flex items-center space-x-2">
            <ProfileImage
              profilePicture={employee.profile_picture || employee.ProfilePicture}
              email={email}
              initials={initials}
              firstName={firstName}
              lastName={lastName}
            />
            <div>
              <span className={invitationStatus === 'pending' ? 'text-gray-500 dark:text-gray-400' : ''}>{displayName}</span>
              <div className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                {statusBadge}
              </div>
            </div>
          </div>
        ),
        Role: invitationStatus === 'active' && employee.role ? (
          <Badge color="blue">{formatRole(employee.role)}</Badge>
        ) : (
          <span className="text-gray-400 dark:text-gray-600 text-xs">—</span>
        ),
        Departments: <div className="flex flex-wrap gap-1">{departmentsList}</div>,
        Teams: <div className="flex flex-wrap gap-1">{teamsList}</div>,
        'Join Date': joinDateStr,
      };
    }).filter((item): item is EmployeeRow => item !== null);
    return mappedData;
  }, [allEmployees, t]);

  const handleTabClick = (value: string) => {
    setActiveTab(value as TabType);
    const params = new URLSearchParams(location.search);
    params.set('tab', value);
    history.push({ search: params.toString() });
  };

return (
  <div className="">
    {isAddingEmployee || isAddingExternal ? (
      <div className='w-full h-screen'>
        {activeTab === 'internal' && <PeopleForm onSuccess={handleCloseOnboarding} onCancel={handleCloseOnboarding} />}
        {activeTab === 'external' && <ExternalOnboarding onClose={handleCloseOnboarding} />}
      </div>
    ) : (
      <>
        <div data-tour-id="people-list-header">
          <Header
            actions={activeTab === 'external' ? [
              {
                label: t('Add External'),
                onClick: handleAddEmployee,
                icon: <PiPlusLight className='w-4 h-4' />,
                requiresSubscription: true,
              },
            ] : []}
            subscriptionActive={subscriptionActive}
          />
        </div>
        <div data-tour-id="people-list-filters">
        <FilterBarWithTabs
          // tabs={tabs}
          // activeTab={activeTab}
          onTabClick={handleTabClick}
          filters={filters}
          selectedFilters={selectedFilters}
          onFilterChange={handleFilterChange}
          onSearchChange={handleSearchChange}
          onClearAllFilters={() => setSelectedFilters({})}
          viewMode={viewMode}
          setViewMode={setViewMode}
        />
        </div>
        <div className="relative">
          <div data-tour-id="people-list-content" className="absolute top-0 left-0 right-0 h-[120px] pointer-events-none" />
          {activeTab === 'internal' && (
            <>

              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-auto max-h-[calc(100vh-120px)] custom-scrollbar-main px-4">
                  {isFetching && allEmployees.length === 0 ? (
                    <SkeletonClientItem viewMode="grid" count={3} />
                  ) : (
                    <>
                      {allEmployees.map((employee) => {
                        const isActive = employee.is_active !== false;
                        const invitationStatus = employee.invitation_status || (isActive ? 'active' : 'pending');
                        const firstName = employee.first_name || employee.FirstName || '';
                        const lastName = employee.last_name || employee.LastName || '';
                        const email = employee.email || employee.Email || '';
                        const displayName = firstName && lastName ? `${firstName} ${lastName}` : email;
                        const initials = firstName && lastName
                          ? `${firstName?.[0] || ''}${lastName?.[0] || ''}`.trim()
                          : (email?.split('@')[0]?.[0] || '?').toUpperCase();
                        const employeeId = employee.id || employee.UserID;

                        if (!employeeId) {
                          console.warn('[WARNING] Employee has no ID:', employee);
                        }

                        const gridDepts = employee.departments && Array.isArray(employee.departments) ? employee.departments : [];
                        const gridTeams = employee.teams && Array.isArray(employee.teams) ? employee.teams : [];

                        const departmentsList = gridDepts.length > 0
                          ? (
                            <>
                              {gridDepts.slice(0, 2).map((d: any) => (
                                <span key={d.id || d.name} className="inline-block mr-2 mb-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                                  {d.name}
                                </span>
                              ))}
                              {gridDepts.length > 2 && (
                                <Tooltip title={gridDepts.slice(2).map((d: any) => d.name).join(', ')} placement="top" arrow>
                                  <span className="inline-block mr-2 mb-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded cursor-default">+{gridDepts.length - 2}</span>
                                </Tooltip>
                              )}
                            </>
                          )
                          : <span className="text-gray-400 dark:text-gray-600 text-xs">—</span>;

                        const teamsList = gridTeams.length > 0
                          ? (
                            <>
                              {gridTeams.slice(0, 2).map((tm: any) => (
                                <span key={tm.id || tm.name} className="inline-block mr-2 mb-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                                  {tm.name}
                                </span>
                              ))}
                              {gridTeams.length > 2 && (
                                <Tooltip title={gridTeams.slice(2).map((tm: any) => tm.name).join(', ')} placement="top" arrow>
                                  <span className="inline-block mr-2 mb-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded cursor-default">+{gridTeams.length - 2}</span>
                                </Tooltip>
                              )}
                            </>
                          )
                          : <span className="text-gray-400 dark:text-gray-600 text-xs">—</span>;

                        return (
                          <EmployeeGridItem
                            key={employeeId}
                            employee={{
                              id: employeeId,
                              Name: (
                                <div className="flex items-center space-x-2">
                                  <ProfileImage
                                    profilePicture={employee.profile_picture || employee.ProfilePicture}
                                    email={email}
                                    initials={initials}
                                    firstName={firstName}
                                    lastName={lastName}
                                  />
                                  <div>
                                    <span className={!isActive ? 'text-gray-500 dark:text-gray-400' : ''}>{displayName}</span>
                                    <div className="text-xs text-gray-600 dark:text-gray-400">
                                      {!isActive && invitationStatus === 'pending' && (
                                        <Badge color="yellow" className="text-xs">{t('Invitation sent')}</Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ),
                              Role: isActive && employee.role ? (
                                <span className="text-sm text-gray-900 dark:text-gray-100">{formatRole(employee.role)}</span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-600 text-xs">—</span>
                              ),
                              Departments: <div className="flex flex-wrap gap-1">{departmentsList}</div>,
                              Teams: <div className="flex flex-wrap gap-1">{teamsList}</div>,
                              'Join Date': employee.created_at ? new Date(employee.created_at).toLocaleDateString() : (employee.JoinDate ? new Date(employee.JoinDate).toLocaleDateString() : 'N/A'),
                            }}
                            onClick={(id) => history.push(`/people/${id}`)}
                          />
                        );
                      })}
                      {allEmployees.length === 0 && (                        <div className="col-span-full flex flex-col justify-center items-center h-full gap-4 max-w-md mx-auto text-center py-28">
                          <div className="text-gray-400 dark:text-gray-500">
                            <PiUsersLight size={48} />
                          </div>
                          <h2 className="text-lg text-gray-900 dark:text-gray-100">
                            {searchTerm || totalActiveFilters > 0 ? t('No results found.') : t('You have no people added yet.')}
                          </h2>
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {searchTerm || totalActiveFilters > 0
                              ? t('Try adjusting your search or filters to find what you’re looking for.')
                              : t('People are invited through the Admin Users section.')}
                          </p>
                          {(searchTerm || totalActiveFilters > 0) && (
                            <Button
                              variant="primary"
                              size="small"
                              onClick={() => setSelectedFilters({})}
                            >
                              {t('Clear Search & Filters')}
                            </Button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {hasMore && allEmployees.length > 0 && (
                    <div className="col-span-full flex justify-center py-6 border-t border-gray-200 dark:border-gray-700">
                      <Button
                        variant="outline"
                        size="small"
                        onClick={loadMoreEmployees}
                        disabled={isFetching}
                        isLoading={isFetching}
                      >
                        {isFetching ? t('Loading...') : t('Load More')}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {isFetching && allEmployees.length === 0 ? (
                    <SkeletonClientItem viewMode="table" count={2} />
                  ) : (
                    <Table
                      columns={columns}
                      data={data}
                      onRowClick={(row) => {
                        const employee = allEmployees.find((emp) => (emp.id || emp.UserID) === row.id);
                        if (employee) {
                          const employeeId = employee.id || employee.UserID;
                          history.push(`/people/${employeeId}`);
                        } else {
                          console.warn('[WARNING] Employee not found for row:', row.id);
                        }
                      }}
                      hasMore={hasMore}
                      isLoadingMore={isFetching && page > 1}
                      onLoadMore={loadMoreEmployees}
                      sortableColumns={['Name', 'Join Date']}
                      sortBy={sortBy}
                      sortOrder={sortOrder}
                      onSort={handleSortChange}
                      resizableColumns={true}
                      setIsShowing={true}
                      total_items={total}
                      total_showing_items={allEmployees.length}
                      showingLabel={t('people')}
                      defaultColumnWidths={{
                        'Name': 260,
                        'Role': 140,
                        'Departments': 240,
                        'Teams': 240,
                        'Join Date': 160,
                      }}
                      emptyState={{
                        icon: <PiUsersLight />,
                        headline: searchTerm || totalActiveFilters > 0 ? t('No results found.') : t('You have no people added yet.'),
                        subtext: searchTerm || totalActiveFilters > 0
                          ? t('Try adjusting your search or filters to find what you’re looking for.')
                          : t('People are invited through the Admin Users section.'),
                        button: searchTerm || totalActiveFilters > 0 ? {
                          label: t('Clear Search & Filters'),
                          onClick: () => setSelectedFilters({}),
                        } : undefined,
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}
          {activeTab === 'external' && <ExternalPeopleTab getQueryParams={getQueryParams} />}
        </div>
        <FeatureTour
          steps={peopleListTourSteps}
          run={isTourRunning}
          onCallback={handleTourCallback}
          continuous
          showProgress
          showSkipButton
          disableOverlay
        />
      </>
    )}
  </div>
);
};

export default EmployeeList;
